import { useEffect, useRef, useState } from "react";
import { checkReportedBefore } from "firebase_/firebase_function/firebase_community";
import { maskMiddleCharacters } from "util/function/function";
import { useUser } from "util/context/user_context";
import moment from "moment";
import ReportModal from "./dialog/report_modal";
import ConfirmDialog from "../confirm_dialog";
import OnlyConfirmDialog from "components/onlyConfirm_dialog";

const C = {
  greenBg: "#ECFDF5",
  greenDark: "#047857",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  red: "#EF4444",
};

const relativeTime = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  if (!m.isValid()) return "";
  const diffMin = moment().diff(m, "minutes");
  if (diffMin < 1) return "방금 전";
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = moment().diff(m, "hours");
  if (diffHr < 24) return `${diffHr}시간 전`;
  const diffDay = moment().diff(m, "days");
  if (diffDay < 7) return `${diffDay}일 전`;
  return m.format("YYYY.MM.DD HH:mm");
};

function Avatar({ name, size = 36 }) {
  const ch = (name || "익").trim().charAt(0);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 9999,
        background: C.greenBg,
        color: C.greenDark,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.42,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {ch}
    </div>
  );
}

function MoreMenu({ items, onClose }) {
  return (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        right: 0,
        minWidth: 110,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        zIndex: 50,
        overflow: "hidden",
      }}
    >
      {items.map((it, i) => (
        <button
          key={it.label}
          type="button"
          onClick={() => {
            it.onClick();
            onClose();
          }}
          style={{
            width: "100%",
            padding: "10px 14px",
            textAlign: "left",
            background: "#fff",
            border: "none",
            borderTop: i === 0 ? "none" : `1px solid ${C.line}`,
            cursor: "pointer",
            fontSize: 13,
            color: it.danger ? C.red : C.body,
            fontWeight: 500,
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export default function CommentBox({
  comment,
  pressedEdit,
  pressedDelete,
  pressedReport,
  pressedBlock,
}) {
  const { userData, blockData } = useUser();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const [editStatus, setEditStatus] = useState(false);
  const [editText, setEditText] = useState("");

  const [reportModal, setReportModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [onlyConfirmModal, setOnlyConfirmModal] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const onClick = (e) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showMenu]);

  if (!blockData) return null;
  if (blockData.some((d) => d.id === comment.userId)) return null;

  const isMine = comment.userId === userData?.id;

  const menuItems = isMine
    ? [
        {
          label: "수정",
          onClick: () => {
            setEditStatus(true);
            setEditText(comment.text);
          },
        },
        {
          label: "삭제",
          onClick: () => pressedDelete(comment.docId),
          danger: true,
        },
      ]
    : [
        {
          label: "신고",
          onClick: async () => {
            const already = await checkReportedBefore(comment.userId, userData.id);
            if (already) {
              setMessage("이미 신고된 유저입니다.");
              setOnlyConfirmModal(true);
            } else {
              setReportModal(true);
            }
          },
        },
        {
          label: "차단",
          onClick: () => setConfirmModal(true),
          danger: true,
        },
      ];

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: 14,
          background: isMine ? "#F8FAF8" : "#fff",
          border: `1px solid ${C.line}`,
          borderRadius: 12,
        }}
      >
        <Avatar name={comment.userName} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>
                {maskMiddleCharacters(comment.userName) || "익명"}
              </span>
              <span style={{ fontSize: 11, color: C.sub }}>
                {relativeTime(comment.createdAt)}
              </span>
              {isMine && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: 4,
                    background: C.greenBg,
                    color: C.greenDark,
                  }}
                >
                  내 댓글
                </span>
              )}
            </div>
            <div ref={menuRef} style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setShowMenu((p) => !p)}
                aria-label="댓글 메뉴"
                style={{
                  width: 28,
                  height: 28,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  borderRadius: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: C.sub,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="6" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="18" r="1.5" />
                </svg>
              </button>
              {showMenu && <MoreMenu items={menuItems} onClose={() => setShowMenu(false)} />}
            </div>
          </div>

          <div style={{ height: 6 }} />

          {!editStatus ? (
            <div
              style={{
                fontSize: 14,
                color: C.body,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {comment.text}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                background: "#FAFAFA",
                border: `1px solid ${C.line}`,
                borderRadius: 10,
                padding: 12,
              }}
            >
              <textarea
                value={editText}
                onChange={(e) => {
                  if (e.target.value.length > 100) {
                    setMessage("댓글은 100글자 이내로 작성해주세요.");
                    setOnlyConfirmModal(true);
                    return;
                  }
                  setEditText(e.target.value);
                }}
                style={{
                  width: "100%",
                  minHeight: 64,
                  resize: "none",
                  outline: "none",
                  border: "none",
                  background: "transparent",
                  fontSize: 14,
                  color: C.ink,
                  lineHeight: 1.6,
                  fontFamily: "inherit",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 11, color: C.sub }}>{editText.length}/100</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => setEditStatus(false)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 6,
                      background: "#fff",
                      border: `1px solid ${C.line}`,
                      color: C.body,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (editText.trim().length === 0) {
                        setMessage("내용이 비어있습니다.");
                        setOnlyConfirmModal(true);
                        return;
                      }
                      pressedEdit(editText, comment.docId);
                      setEditStatus(false);
                    }}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 6,
                      background: C.greenDark,
                      border: "none",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    수정
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ReportModal
        open={reportModal}
        onClose={() => setReportModal(false)}
        onSubmit={pressedReport}
        targetId={comment.userId}
        targetName={comment.userName}
      />
      <ConfirmDialog
        open={confirmModal}
        onClose={() => setConfirmModal(false)}
        onConfirm={() => {
          pressedBlock(comment.userId, comment.userName, userData.id, userData.docId);
          setConfirmModal(false);
        }}
        message={"차단 하시겠습니까?"}
      />
      <OnlyConfirmDialog
        open={onlyConfirmModal}
        onClose={() => setOnlyConfirmModal(false)}
        message={message}
      />
    </>
  );
}
